/**
 * API Call Manager - Handles request cancellation and deduplication
 * Prevents multiple API calls and provides proper cleanup
 */

class ApiCallManager {
  constructor() {
    this.activeRequests = new Map()
    this.requestQueue = new Map()
  }

  /**
   * Create a unique key for the request
   */
  createRequestKey(url, params = {}) {
    const sortedParams = Object.keys(params)
      .sort()
      .reduce((result, key) => {
        result[key] = params[key]
        return result
      }, {})

    return `${url}:${JSON.stringify(sortedParams)}`
  }

  /**
   * Execute API call with cancellation and deduplication
   */
  async executeRequest(requestKey, apiCall, options = {}) {
    const {
      timeout = 30000,
      retryCount = 0,
      onStart = null,
      onComplete = null,
      onError = null
    } = options

    void retryCount // reserved for future retries

    // Check if the same request is already in progress
    if (this.activeRequests.has(requestKey)) {
      console.log(`Request already in progress: ${requestKey}`)
      return this.activeRequests.get(requestKey).promise
    }

    // Create AbortController for cancellation
    const abortController = new AbortController()
    const timeoutId = setTimeout(() => {
      abortController.abort()
    }, timeout)

    // Create the request promise
    const requestPromise = this.createRequestPromise(
      apiCall,
      abortController.signal,
      timeoutId,
      onStart,
      onComplete,
      onError
    )

    // Store the active request metadata
    this.activeRequests.set(requestKey, {
      promise: requestPromise,
      abortController,
      timeoutId
    })

    try {
      const result = await requestPromise
      return result
    } finally {
      clearTimeout(timeoutId)
      const active = this.activeRequests.get(requestKey)
      if (active?.promise === requestPromise) {
        this.activeRequests.delete(requestKey)
      }
    }
  }

  /**
   * Create the actual request promise with proper error handling
   */
  async createRequestPromise(apiCall, signal, timeoutId, onStart, onComplete, onError) {
    try {
      if (onStart) onStart()

      const result = await apiCall(signal)

      if (onComplete) onComplete(result)
      return result
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('Request was cancelled')
        throw new Error('Request cancelled')
      }

      if (onError) onError(error)
      throw error
    } finally {
      clearTimeout(timeoutId)
    }
  }

  /**
   * Cancel a specific request
   */
  cancelRequest(requestKey) {
    const active = this.activeRequests.get(requestKey)
    if (active) {
      console.log(`Cancelling request: ${requestKey}`)
      clearTimeout(active.timeoutId)
      active.abortController.abort()
      this.activeRequests.delete(requestKey)
    }
  }

  /**
   * Cancel all active requests
   */
  cancelAllRequests() {
    console.log(`Cancelling ${this.activeRequests.size} active requests`)
    this.activeRequests.forEach((active) => {
      clearTimeout(active.timeoutId)
      active.abortController.abort()
    })
    this.activeRequests.clear()
  }

  /**
   * Get active request count
   */
  getActiveRequestCount() {
    return this.activeRequests.size
  }

  /**
   * Check if a request is active
   */
  isRequestActive(requestKey) {
    return this.activeRequests.has(requestKey)
  }

  /**
   * Debounced request execution
   */
  debounceRequest(requestKey, apiCall, delay = 300, options = {}) {
    // Clear existing timeout for this request
    if (this.requestQueue.has(requestKey)) {
      clearTimeout(this.requestQueue.get(requestKey))
    }

    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(async () => {
        this.requestQueue.delete(requestKey)
        try {
          const result = await this.executeRequest(requestKey, apiCall, options)
          resolve(result)
        } catch (error) {
          reject(error)
        }
      }, delay)

      this.requestQueue.set(requestKey, timeoutId)
    })
  }

  /**
   * Clear debounced requests
   */
  clearDebouncedRequests() {
    this.requestQueue.forEach(timeoutId => clearTimeout(timeoutId))
    this.requestQueue.clear()
  }
}

// Create singleton instance
const apiCallManager = new ApiCallManager()

export default apiCallManager

/**
 * Hook for React components to use the API call manager
 */
export const useApiCallManager = () => {
  const executeRequest = (requestKey, apiCall, options = {}) => {
    return apiCallManager.executeRequest(requestKey, apiCall, options)
  }

  const debounceRequest = (requestKey, apiCall, delay = 300, options = {}) => {
    return apiCallManager.debounceRequest(requestKey, apiCall, delay, options)
  }

  const cancelRequest = (requestKey) => {
    return apiCallManager.cancelRequest(requestKey)
  }

  const cancelAllRequests = () => {
    return apiCallManager.cancelAllRequests()
  }

  const isRequestActive = (requestKey) => {
    return apiCallManager.isRequestActive(requestKey)
  }

  return {
    executeRequest,
    debounceRequest,
    cancelRequest,
    cancelAllRequests,
    isRequestActive,
    getActiveRequestCount: () => apiCallManager.getActiveRequestCount()
  }
}
